/*
 * Every Rod Armour video starts muted. window.raPlayMuted(video) mutes it (attribute and property,
 * before play), plays it inline and puts an "Unmute" button over its top corner. Sound comes on
 * only when the shopper presses that button or the player's own volume control.
 * Locked pages (Curtis, 2026-10-16: "always mute this video on the LureShield page and make it not be able
 * to be unmuted"): on the LureShield template, or anywhere under [data-ra-mute-lock] / body.ra-mute-lock,
 * videos stay muted for good: no Unmute button, no native controls (they include a volume button),
 * any attempt to unmute is reverted, and a tap on the video plays or pauses it.
 * Keyboard (B14, Curtis 2026-10-19): once a video without the player's own controls starts (the buy-box galleries
 * and every locked video), a Play/Pause button stays over its top-left corner, reachable with Tab, and its name
 * follows the video ("Pause video" while it plays, "Play video" while it is paused). The mute lock is unchanged.
 * One mute control at a time (2026-10-21): raPlayMuted(v, { controlsLater: true }) holds back the player's own controls
 * (their volume button sat under the Unmute button) while the video plays muted with Unmute + Play/Pause; the first
 * unmute or tap on the video brings the player's own controls back and takes those two buttons away.
 * Used by the buy-box galleries (ra-nb2.js, ra-reel-buybox.js) and the click-to-play players
 * (ra-pdp-video.js). The parent of the video must be positioned.
 */
(() => {
  if (window.raPlayMuted) return;

  const css = `
.ra-unmute{position:absolute;inset-block-start:10px;inset-inline-end:10px;z-index:2;display:inline-flex;align-items:center;gap:6px;min-block-size:44px;padding:0 14px;border:0;border-radius:999px;background:oklch(20% 0.06 265 / .82);color:oklch(100% 0 none);font:700 14px/1 var(--font-body--family,system-ui,sans-serif);letter-spacing:.02em;cursor:pointer}
.ra-unmute svg{inline-size:18px;block-size:18px;fill:none;stroke:currentColor;stroke-width:2;stroke-linecap:round;stroke-linejoin:round}
.ra-unmute:focus-visible{outline:2px solid oklch(100% 0 none);outline-offset:2px}
.ra-unmute:active{background:oklch(20% 0.06 265 / .95)}
.ra-vpp{position:absolute;inset-block-start:10px;inset-inline-start:10px;z-index:2;display:inline-flex;align-items:center;justify-content:center;inline-size:44px;block-size:44px;padding:0;border:0;border-radius:999px;background:oklch(20% 0.06 265 / .82);color:oklch(100% 0 none);cursor:pointer}
.ra-vpp svg{inline-size:18px;block-size:18px;fill:currentColor;stroke:currentColor;stroke-width:2;stroke-linecap:round;stroke-linejoin:round}
.ra-vpp:focus-visible{outline:2px solid oklch(100% 0 none);outline-offset:2px}
.ra-vpp:active{background:oklch(20% 0.06 265 / .95)}
@media (hover:hover) and (pointer:fine){.ra-unmute:hover,.ra-vpp:hover{background:oklch(20% 0.06 265 / .95)}}`;
  // Constructable stylesheets need Safari 16.4+; older WebKit (iOS 15 Facebook/Instagram in-app browsers) throws
  // "Illegal constructor", which stopped the whole file, mute lock included (review pass 3). A <style> there instead.
  try {
    const sheet = new CSSStyleSheet();
    sheet.replaceSync(css);
    document.adoptedStyleSheets = [...document.adoptedStyleSheets, sheet];
  } catch {
    const style = document.createElement('style');
    style.textContent = css;
    document.head.append(style);
  }

  const ICON_OFF = '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M11 5 6 9H3v6h3l5 4z"/><path d="m22 9-6 6M16 9l6 6"/></svg>';
  const ICON_ON = '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M11 5 6 9H3v6h3l5 4z"/><path d="M15.5 8.5a5 5 0 0 1 0 7M18.5 5.5a9 9 0 0 1 0 13"/></svg>';
  const ICON_PLAY = '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M7 4.5v15l12-7.5z"/></svg>';
  const ICON_PAUSE = '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M7 5h3v14H7zM14 5h3v14h-3z"/></svg>';

  /** @param {HTMLVideoElement} v */
  const mute = (v) => {
    v.setAttribute('muted', '');
    v.setAttribute('playsinline', '');
    v.defaultMuted = true;
    v.muted = true;
    v.playsInline = true;
  };

  /** @param {HTMLVideoElement} v */
  const button = (v) => {
    const host = v.parentElement;
    if (!host) return;
    let b = host.querySelector(':scope > .ra-unmute');
    if (!b) {
      b = document.createElement('button');
      b.type = 'button';
      b.className = 'ra-unmute';
      b.addEventListener('click', (e) => {
        e.stopPropagation();
        v.muted = !v.muted;
        if (!v.muted && v.paused) v.play().catch(() => {});
      });
      v.addEventListener('volumechange', () => paint(v, b));
      host.append(b);
    }
    paint(v, b);
  };

  const paint = (v, b) => {
    b.innerHTML = v.muted ? `${ICON_OFF}<span>Unmute</span>` : `${ICON_ON}<span>Mute</span>`;
    b.setAttribute('aria-label', v.muted ? 'Unmute video' : 'Mute video');
  };

  /** Play/Pause for keyboard users, on videos without the player's own controls (those have their own).
   *  @param {HTMLVideoElement} v */
  const playPause = (v) => {
    const host = v.parentElement;
    if (!host || v.controls) return;
    let b = host.querySelector(':scope > .ra-vpp');
    if (!b) {
      b = document.createElement('button');
      b.type = 'button';
      b.className = 'ra-vpp';
      b.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (v.paused) v.play().catch(() => {});
        else v.pause();
      });
      const sync = () => paintPlay(v, b);
      for (const ev of ['play', 'playing', 'pause', 'ended']) v.addEventListener(ev, sync);
      host.append(b);
    }
    paintPlay(v, b);
  };

  const paintPlay = (v, b) => {
    const label = v.paused ? 'Play video' : 'Pause video';
    if (b.getAttribute('aria-label') === label) return;
    b.innerHTML = v.paused ? ICON_PLAY : ICON_PAUSE;
    b.setAttribute('aria-label', label);
  };

  const LOCK = '.ra-t-product-lureshield, .ra-mute-lock';
  /** @param {Element} v */
  const locked = (v) => document.body.matches(LOCK) || !!v.closest('[data-ra-mute-lock]');

  /** @param {HTMLVideoElement} v */
  const lock = (v) => {
    mute(v);
    v.controls = false;
    v.removeAttribute('controls');
    v.parentElement?.querySelector(':scope > .ra-unmute')?.remove();
    if (v.dataset.raLocked) return;
    v.dataset.raLocked = '1';
    v.setAttribute('aria-label', 'Video without sound. Tap to play or pause.');
    v.style.cursor = 'pointer';
    v.addEventListener('volumechange', () => { if (!v.muted || v.volume > 0) { v.muted = true; } });
    v.addEventListener('click', (e) => { e.preventDefault(); if (v.paused) v.play().catch(() => {}); else v.pause(); });
  };

  // Catch every video on a locked page, including ones other scripts add or unmute later.
  const sweep = () => {
    for (const v of document.querySelectorAll('video')) if (locked(v)) lock(v);
  };
  document.addEventListener('volumechange', (e) => {
    const v = e.target;
    if (v instanceof HTMLVideoElement && locked(v) && !v.muted) v.muted = true;
  }, true);
  document.addEventListener('play', (e) => {
    const v = e.target;
    if (v instanceof HTMLVideoElement && locked(v)) {
      lock(v);
      playPause(v);
    }
  }, true);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', sweep, { once: true });
  else sweep();
  if (document.body.matches(LOCK)) new MutationObserver(sweep).observe(document.body, { childList: true, subtree: true });

  /** The player's own controls back, the Unmute and Play/Pause buttons gone (controlsLater).
   *  @param {HTMLVideoElement} v */
  const reveal = (v) => {
    if (!v.dataset.raControlsLater || locked(v)) return;
    delete v.dataset.raControlsLater;
    const host = v.parentElement;
    const extra = host ? [host.querySelector(':scope > .ra-unmute'), host.querySelector(':scope > .ra-vpp')] : [];
    const hadFocus = extra.some((b) => b && document.activeElement === b);
    for (const b of extra) b?.remove();
    v.controls = true;
    v.setAttribute('controls', '');
    if (hadFocus) v.focus({ preventScroll: true });
  };

  /** @param {HTMLVideoElement} v */
  const controlsLater = (v) => {
    if (!v.controls) return;
    v.controls = false;
    v.removeAttribute('controls');
    if (v.dataset.raControlsLater) return;
    v.dataset.raControlsLater = '1';
    v.addEventListener('volumechange', () => { if (!v.muted) reveal(v); });
    v.addEventListener('click', () => reveal(v));
  };

  /** @param {HTMLVideoElement | null | undefined} v @param {{ controlsLater?: boolean }} [opts] */
  window.raPlayMuted = (v, opts) => {
    if (!v) return;
    if (locked(v)) {
      lock(v);
      playPause(v);
      const p = v.play();
      if (p && p.catch) p.catch(() => {});
      return;
    }
    mute(v);
    if (opts && opts.controlsLater) controlsLater(v);
    button(v);
    playPause(v);
    const p = v.play();
    if (p && p.catch) p.catch(() => {});
  };
  window.raMuteVideo = mute;
  window.raVideoLocked = locked;
})();
